import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import BootstrapTable from "react-bootstrap-table-next"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import {
  GetPartnerCockpitDetails,
  InviteUserToPartnerCockpit,
  GetPartnerCockpitInvites,
  UpdatePartnerCockpitUser,
  RemovePartnerCockpitUser,
  GetMyUser,
  DeletePartnerCockpit,
} from "helpers/backend_helper"
import { formatDate } from "helpers/misc_helpers"

const PartnerCockpitDetails = ({ t }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cockpit, setCockpit] = useState(null)
  const [addUserModal, setAddUserModal] = useState(false)
  const [invitesModal, setInvitesModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [roleModal, setRoleModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newUserEmail, setNewUserEmail] = useState("")
  const [newUserType, setNewUserType] = useState("user")
  const [user, setUser] = useState(null)
  const [inviteStatus, setInviteStatus] = useState({
    loading: false,
    error: null,
    success: false,
  })
  const [pendingInvites, setPendingInvites] = useState([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const [deleteCockpitModal, setDeleteCockpitModal] = useState(false)

  useEffect(() => {
    fetchCockpitDetails()
  }, [id])

  useEffect(() => {
    setLoading(true)
    GetMyUser().then(user => {
      setUser(user)
      setLoading(false)
    })
  }, [])

  const fetchCockpitDetails = async () => {
    try {
      setLoading(true)
      const response = await GetPartnerCockpitDetails(id)
      setCockpit(response)
    } catch (error) {
      setError(t("Failed to fetch partner cockpit details"))
      console.error("Error fetching partner cockpit details:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPendingInvites = async () => {
    try {
      setInvitesLoading(true)
      const response = await GetPartnerCockpitInvites(id)
      setPendingInvites(response)
    } catch (error) {
      console.error("Error fetching pending invites:", error)
    } finally {
      setInvitesLoading(false)
    }
  }

  const toggleAddUserModal = () => {
    setAddUserModal(!addUserModal)
    if (!addUserModal) {
      setInviteStatus({ loading: false, error: null, success: false })
      setNewUserEmail("")
      setNewUserType("user")
    }
  }

  const toggleInvitesModal = async () => {
    if (!invitesModal) {
      await fetchPendingInvites()
    }
    setInvitesModal(!invitesModal)
  }

  const toggleDeleteModal = (user = null) => {
    setSelectedUser(user)
    setDeleteModal(!deleteModal)
  }

  const toggleRoleModal = (user = null) => {
    setSelectedUser(user)
    setRoleModal(!roleModal)
  }

  const toggleDeleteCockpitModal = () => {
    setDeleteCockpitModal(!deleteCockpitModal)
  }

  const handleInviteUser = async () => {
    if (!newUserEmail.trim()) {
      setInviteStatus({
        loading: false,
        error: t("Please enter an email address"),
        success: false,
      })
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newUserEmail)) {
      setInviteStatus({
        loading: false,
        error: t("Please enter a valid email address"),
        success: false,
      })
      return
    }

    try {
      setInviteStatus({ loading: true, error: null, success: false })
      await InviteUserToPartnerCockpit(id, newUserEmail, newUserType)
      setInviteStatus({ loading: false, error: null, success: true })
      setNewUserEmail("")
      setTimeout(() => {
        toggleAddUserModal()
        fetchCockpitDetails()
      }, 2000)
    } catch (error) {
      console.error("Error inviting user:", error)
      setInviteStatus({
        loading: false,
        error: error.response?.data?.error || t("Failed to invite user"),
        success: false,
      })
    }
  }

  const handleDeleteUser = async () => {
    try {
      await RemovePartnerCockpitUser(id, selectedUser.id)
      toggleDeleteModal()
      fetchCockpitDetails()
    } catch (error) {
      console.error("Error removing user:", error)
    }
  }

  const handleChangeRole = async () => {
    try {
      await UpdatePartnerCockpitUser(id, selectedUser.id, {
        role: selectedUser.role,
      })
      toggleRoleModal()
      fetchCockpitDetails()
    } catch (error) {
      console.error("Error updating user role:", error)
    }
  }

  const handleDeleteCockpit = async () => {
    try {
      await DeletePartnerCockpit(id)
      navigate("/partner-cockpit")
    } catch (error) {
      console.error("Error deleting partner cockpit:", error)
    }
  }

  const getRoleBadgeColor = role => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "danger"
      case "user":
        return "primary"
      case "moderator":
        return "warning"
      default:
        return "secondary"
    }
  }

  const isAdmin = row => {
    // if (!cockpit || !user) return false
    // const currentUser = cockpit.users.find(u => u.id === user.id)
    // return currentUser?.role === "admin"

    return cockpit?.users?.find(u => u.id === user?.id)?.role === "Admin"
  }

  const columns = [
    {
      dataField: "companyName",
      text: t("Company"),
      sort: true,
    },
    {
      dataField: "name",
      text: t("User"),
      sort: true,
      formatter: (cell, row) => `${row?.firstname} ${row?.lastname}`,
    },
    {
      dataField: "role",
      text: t("Role"),
      sort: true,
      formatter: cell => (
        <Badge color={getRoleBadgeColor(cell)} className="font-size-12">
          {cell}
        </Badge>
      ),
    },
    {
      dataField: "joinedAt",
      text: t("Joined At"),
      sort: true,
      formatter: cell => formatDate(cell),
    },
    {
      dataField: "actions",
      text: t(""),
      formatter: (cell, row) => {
        if (!isAdmin(row) || row.id === user?.id) return null
        return (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => toggleRoleModal(row)}
            >
              {t("Change Role")}
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => toggleDeleteModal(row)}
            >
              {t("Remove")}
            </Button>
          </div>
        )
      },
    },
  ]

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center">
            <Spinner color="primary" />
            <span className="ms-2">{t("Loading...")}</span>
          </div>
        </CardBody>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <div className="text-center text-danger">{error}</div>
        </CardBody>
      </Card>
    )
  }

  if (!cockpit) {
    return (
      <Card>
        <CardBody>
          <div className="text-center">{t("Partner cockpit not found")}</div>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={"Partner Cockpit"}
          breadcrumbItem={"Details"}
          showBackButton={true}
        />
        <div className="row">
          <div className="col-12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">{cockpit.name}</h4>
                  <div className="d-flex align-items-center">
                    <div className="text-muted me-3">
                      {t("Created at")}: {formatDate(cockpit.createdAt)}
                    </div>
                    {isAdmin() && (
                      <>
                        <Button
                          color="info"
                          className="me-2"
                          onClick={toggleInvitesModal}
                        >
                          <i className="fas fa-envelope me-2"></i>
                          {t("Pending Invites")}
                        </Button>
                        <Button
                          color="primary"
                          className="me-2"
                          onClick={toggleAddUserModal}
                        >
                          <i className="fas fa-user-plus me-2"></i>
                          {t("Invite User")}
                        </Button>
                        <Button
                          color="danger"
                          onClick={toggleDeleteCockpitModal}
                        >
                          <i className="fas fa-trash me-2"></i>
                          {t("Delete Cockpit")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <h5 className="mb-3">{t("Members")}</h5>
                {user && (
                  <ToolkitProvider
                    keyField="id"
                    data={cockpit.users}
                    columns={columns}
                    bootstrap4
                    search
                  >
                    {toolkitProps => (
                      <div className="table-responsive">
                        <BootstrapTable
                          {...toolkitProps.baseProps}
                          bordered={false}
                          striped
                          hover
                          classes={"table align-middle table-nowrap"}
                          headerWrapperClasses={"table-light"}
                        />
                      </div>
                    )}
                  </ToolkitProvider>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Invite User Modal */}
      <Modal isOpen={addUserModal} toggle={toggleAddUserModal}>
        <ModalHeader toggle={toggleAddUserModal}>
          {t("Invite User to Partner Cockpit")}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="userEmail">{t("Email Address")}</Label>
              <Input
                type="email"
                name="userEmail"
                id="userEmail"
                value={newUserEmail}
                onChange={e => setNewUserEmail(e.target.value)}
                placeholder={t("Enter email address")}
                disabled={inviteStatus.loading}
              />
            </FormGroup>
            {/* <FormGroup>
              <Label for="userType">{t("User Type")}</Label>
              <Input
                type="select"
                name="userType"
                id="userType"
                value={newUserType}
                onChange={e => setNewUserType(e.target.value)}
                disabled={inviteStatus.loading}
              >
                <option value="user">{t("User")}</option>
                <option value="admin">{t("Admin")}</option>
              </Input>
            </FormGroup> */}
            {inviteStatus.error && (
              <div className="text-danger mt-2">{inviteStatus.error}</div>
            )}
            {inviteStatus.success && (
              <div className="text-success mt-2">
                {t("Invitation sent successfully")}
              </div>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleAddUserModal}
            disabled={inviteStatus.loading}
          >
            {t("Cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handleInviteUser}
            disabled={inviteStatus.loading || inviteStatus.success}
          >
            {inviteStatus.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("Sending...")}
              </>
            ) : (
              t("Send Invitation")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>
          {t("Confirm User Removal")}
        </ModalHeader>
        <ModalBody>
          {t("Are you sure you want to remove")}{" "}
          {selectedUser
            ? `${selectedUser.firstname} ${selectedUser.lastname}`
            : ""}{" "}
          {t("from this partner cockpit?")}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            {t("Cancel")}
          </Button>
          <Button color="danger" onClick={handleDeleteUser}>
            {t("Remove")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Change Role Modal */}
      <Modal isOpen={roleModal} toggle={toggleRoleModal}>
        <ModalHeader toggle={toggleRoleModal}>
          {t("Change User Role")}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="userRole">{t("Select Role")}</Label>
              <Input
                type="select"
                name="userRole"
                id="userRole"
                value={selectedUser?.role}
                onChange={e =>
                  setSelectedUser({ ...selectedUser, role: e.target.value })
                }
              >
                <option value="user">{t("User")}</option>
                <option value="admin">{t("Admin")}</option>
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleRoleModal}>
            {t("Cancel")}
          </Button>
          <Button color="primary" onClick={handleChangeRole}>
            {t("Save Changes")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Pending Invites Modal */}
      <Modal isOpen={invitesModal} toggle={toggleInvitesModal} size="lg">
        <ModalHeader toggle={toggleInvitesModal}>
          {t("Pending Invites")}
        </ModalHeader>
        <ModalBody>
          {invitesLoading ? (
            <div className="text-center">
              <Spinner color="primary" />
              <span className="ms-2">{t("Loading...")}</span>
            </div>
          ) : (
            <div className="table-responsive">
              <BootstrapTable
                keyField="id"
                data={pendingInvites}
                columns={[
                  {
                    dataField: "email",
                    text: t("Email"),
                  },
                  {
                    dataField: "status",
                    text: t("Status"),
                    formatter: cell => {
                      if (cell === "pending") {
                        return <Badge color="warning">{t("Pending")}</Badge>
                      }
                      return <Badge color="success">{t("Accepted")}</Badge>
                    },
                  },
                  {
                    dataField: "invitedAt",
                    text: t("Invited At"),
                    formatter: cell => formatDate(cell),
                  },
                ]}
                bordered={false}
                striped
                hover
                classes={"table align-middle table-nowrap"}
                headerWrapperClasses={"table-light"}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleInvitesModal}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Cockpit Confirmation Modal */}
      <Modal isOpen={deleteCockpitModal} toggle={toggleDeleteCockpitModal}>
        <ModalHeader toggle={toggleDeleteCockpitModal}>
          {t("Confirm Deletion")}
        </ModalHeader>
        <ModalBody>
          {t(
            "Are you sure you want to delete this partner cockpit? This action cannot be undone."
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteCockpitModal}>
            {t("Cancel")}
          </Button>
          <Button color="danger" onClick={handleDeleteCockpit}>
            {t("Delete")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default withTranslation()(PartnerCockpitDetails)
